import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback} from 'react';

import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {CHAT_INPUT_TOOLBAR_CONTROL_HEIGHT_PX} from 'llm/chat-library/ui/AIStyleConstants';
import WorkspaceStore, {WorkspaceItemVariant} from 'sidebar/WorkspaceStore';
import BenchChip, {isModelBenchChipSupported} from 'ui-library/chips/BenchChip';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';
import {apiIdToIconName} from 'util/APIUtil';

import {useWorkspaceContextIds} from './useWorkspaceContext';

interface WorkspaceContextChipProps {
  agentType: AgentType | null;
}

export default function WorkspaceContextChip({agentType}: WorkspaceContextChipProps): JSX.Element | null {
  const contextIds = useWorkspaceContextIds(agentType);

  const workspaceNameById = useStoreSelector(
    WorkspaceStore,
    useCallback(
      (store: typeof WorkspaceStore) =>
        new Map(
          store.workspaceItems
            .filter((item: WorkspaceItemVariant) => contextIds.includes(item.fileId))
            .map((item: WorkspaceItemVariant) => [item.fileId, item.name] as const)
        ),
      [contextIds]
    )
  );

  if (contextIds.length === 0) {
    return null;
  }

  const chipClassName = css(styles.chip);

  return (
    <div className={css(styles.contextSection)}>
      {contextIds.map((contextId) =>
        isModelBenchChipSupported(contextId) ? (
          <BenchChip
            key={contextId}
            className={chipClassName}
            size={ChipSize.MINI}
            id={contextId}
            isLink={false}
          />
        ) : (
          <Chip
            key={contextId}
            className={chipClassName}
            size={ChipSize.MINI}
            text={workspaceNameById.get(contextId) || 'Untitled'}
            icon={apiIdToIconName(contextId)}
          />
        )
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  contextSection: {
    display: 'flex',
    gap: '4px',
    padding: '12px 12px 0',
    overflow: 'hidden',
  },
  chip: {
    fontSize: '12px',
    whiteSpace: 'nowrap',
    marginRight: 0,
    minWidth: 0,
    boxSizing: 'border-box',
    minHeight: `${CHAT_INPUT_TOOLBAR_CONTROL_HEIGHT_PX}px`,
    height: `${CHAT_INPUT_TOOLBAR_CONTROL_HEIGHT_PX}px`,
    display: 'inline-flex',
    alignItems: 'center',
    color: Color.BLUE_450,
    // BenchChip / Chip nest label + icon under `.chip-*` classes with global near-black defaults.
    ' .chip-icon': {
      color: `${Color.BLUE_450} !important`,
    },
    ' svg.chip-icon': {
      color: `${Color.BLUE_450} !important`,
    },
    ' .chip-text': {
      color: `${Color.BLUE_450} !important`,
    },
    ' .chip-text-secondaryText': {
      color: `${Color.BLUE_450} !important`,
    },
    ' a': {
      color: `${Color.BLUE_450} !important`,
    },
  },
});
